import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { DataAmount, HeadingS, SmallText, Spinner } from '@toqio/toqio-web-components';
import { useBridge } from '../../../bridge/useBridge';
import { useFlowReady } from '../../../bridge/useFlowReady';
import { fetchPendingPayments, parseApiError, submitDecision } from '../api';
import type { Decision, PendingPayment } from '../schema';
import {
  ButtonsLayout,
  CenteredPlaceholder,
  CircularTimer,
  EmptyState,
  PageContainer,
  PageHeader,
  PrimaryButton,
  SecondaryButton,
} from '../../../shared/components';
import { FLOW_ID, flowPath, formatAmount } from '../shared';
import type { ResultBannerState } from './PendingList';

type Phase = 'loading' | 'ready' | 'error';

// Too little time left to realistically approve (the decision POST needs a
// round-trip) → treat as gone instead of flashing a doomed countdown.
const MIN_ACTIONABLE_SECONDS = 3;

// Inherits the .lum-web-container page width (1140px) and responsive gutter
// from PageContainer; top spacing comes from PageHeader's own margin.
// On mobile, fill the viewport so the actions can sink to the bottom (SEB-1616).
const Page = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media (max-width: 574px) {
    min-height: 100dvh;
    padding-bottom: 20px;
  }
`;

// Buttons sit on a 12px row gap (Flowable `.button-container`). The large
// desktop gap above them is Flowable's `.spacer-before-buttons` (108px); 84px +
// the Page's 24px gap = 108px. On mobile they stack with a wider 24px gap and
// sink to the bottom of the screen, staying aligned with the rest of the
// content (same PageContainer gutter) — SEB-1616.
const Actions = styled(ButtonsLayout)`
  gap: 12px;
  margin-top: 84px;
  @media (max-width: 574px) {
    flex-direction: column;
    gap: 24px;
    margin-top: auto;
  }
`;

// Mirrors Flowable's `.details-container__row`: merchant left, amount right,
// bottom-aligned (SEB-1616).
const Body = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
`;

const Merchant = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AmountBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const CenteredSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 56px 0;
`;

export const ApprovePayment: React.FC = () => {
  const bridge = useBridge();
  const navigate = useNavigate();
  const { notificationId } = useParams<{ notificationId: string }>();
  const { signalReady } = useFlowReady();

  const [phase, setPhase] = useState<Phase>('loading');
  const [payment, setPayment] = useState<PendingPayment | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expired, setExpired] = useState(false);

  const locale = bridge.locale ?? 'en';

  const goToList = useCallback(
    (state?: ResultBannerState) => {
      navigate(flowPath('pending'), state ? { state } : undefined);
    },
    [navigate],
  );

  // Emit step:change once on mount (bypassing StepWizard).
  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'approve-payment',
      stepIndex: 0,
      totalSteps: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Host back signal → return to the list in-app (no decision sent).
  useEffect(() => {
    return bridge.onBackRequest(() => goToList());
  }, [bridge, goToList]);

  const showError = useCallback((message: string) => {
    setErrorMessage(message);
    setPhase('error');
  }, []);

  const load = useCallback(async () => {
    if (!notificationId) {
      showError(t`Payment request not found.`);
      return;
    }
    setPhase('loading');
    try {
      const items = await fetchPendingPayments();
      const found = items.find((p) => p.id === notificationId);
      if (!found || found.timerInfo.timeRemaining < MIN_ACTIONABLE_SECONDS) {
        showError(t`Payment request not found.`);
        return;
      }
      setPayment(found);
      setPhase('ready');
    } catch {
      // Any failure to load the payment surfaces as the Flowable detail error
      // placeholder — there's a single copy for it (SEB-1618).
      showError(t`Payment request not found.`);
    }
  }, [notificationId, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  // Drop the host overlay once the entry fetch settles (manual readiness).
  useEffect(() => {
    if (phase === 'loading') return;
    signalReady();
  }, [phase, signalReady]);

  const decide = useCallback(
    async (decision: Decision) => {
      if (!notificationId || submitting || expired) return;
      setSubmitting(true);
      bridge.emit({
        type: 'telemetry',
        event: 'card-payment-sca.decision.clicked',
        props: { notification_id: notificationId, decision },
      });
      try {
        const result = await submitDecision(notificationId, decision, payment?.title);
        bridge.emit({
          type: 'telemetry',
          event: 'card-payment-sca.decision.success',
          props: { notification_id: notificationId, decision },
        });
        goToList({
          status: 'success',
          message:
            result.message ??
            (decision === 'APPROVED' ? t`Payment approved.` : t`Payment declined.`),
        });
      } catch (e) {
        const { status, errorCode, message, detail } = parseApiError(e);
        // Surface the backend-authored copy verbatim (no front-side mapping);
        // fall back to a generic line when no response body reached us
        // (e.g. a 502 stripped of CORS headers arrives as a network error).
        const backendMsg = message ?? detail;
        bridge.emit({
          type: 'telemetry',
          event: 'card-payment-sca.decision.error',
          props: {
            notification_id: notificationId,
            error_code: errorCode ?? (status ? String(status) : 'TRANSIENT'),
          },
        });
        // Every decision failure shows the error placeholder on this screen
        // (Flowable `emptyState` error) with a Back-to-list action — not a list
        // banner (SEB-1618). The BE message carries the exact `messageByError`
        // copy; fall back to Flowable's GENERAL_ERROR line when no body reached
        // us (e.g. a network error).
        showError(
          backendMsg ??
            (decision === 'APPROVED'
              ? t`Approval failed. Please try again.`
              : t`Decline failed. Please try again.`),
        );
      }
    },
    [notificationId, submitting, expired, bridge, goToList, showError, payment?.title],
  );

  if (phase === 'loading') {
    return (
      <Page>
        <CenteredSpinner>
          <Spinner />
        </CenteredSpinner>
      </Page>
    );
  }

  if (phase === 'error' || !payment) {
    return (
      <Page>
        <PageHeader
          title={t`Review Payment`}
          showDivider={false}
          showBackButton
          bottomSpacing={0}
          onClickBack={() => goToList()}
        />
        <CenteredPlaceholder>
          <EmptyState
            variant="error"
            title={t`Error`}
            description={errorMessage || t`Payment request not found.`}
            action={{
              label: t`Back to the list`,
              onClick: () => goToList(),
              variant: 'primary',
            }}
          />
        </CenteredPlaceholder>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title={t`Review Payment`}
        showDivider={false}
        showBackButton
        bottomSpacing={0}
        onClickBack={() => goToList()}
      />

      <CircularTimer
        timeRemaining={payment.timerInfo.timeRemaining}
        timeFull={payment.timerInfo.timeFull}
        size={64}
        onExpire={() => {
          setExpired(true);
          goToList();
        }}
      />

      <Body>
        <Merchant>
          <HeadingS>{payment.title}</HeadingS>
          {payment.transactionChannel && <SmallText>{payment.transactionChannel}</SmallText>}
        </Merchant>
        <AmountBlock>
          <DataAmount
            label=""
            value={formatAmount(payment.amountNum, locale)}
            currency={payment.currency}
            size="m"
            weight="bold"
          />
          <SmallText>
            <Trans>Requires Approval</Trans>
          </SmallText>
        </AmountBlock>
      </Body>

      <Actions>
        <PrimaryButton
          disabled={submitting || expired}
          onClick={() => void decide('APPROVED')}
        >
          <Trans>Approve</Trans>
        </PrimaryButton>
        <SecondaryButton
          disabled={submitting || expired}
          onClick={() => void decide('DECLINED')}
        >
          <Trans>Decline</Trans>
        </SecondaryButton>
      </Actions>
    </Page>
  );
};
